import { useCallback, useEffect, useState } from "react";
import { BriefcaseBusiness, ClipboardList, Download, LogOut, Mail, MapPin, Pencil, Plus, Trash2, UsersRound } from "lucide-react";
import { Navigate } from "react-router-dom";
import { apiRequest, useAdminAuth } from "../auth/AdminAuthContext";
import AdminJobEditor from "../components/AdminJobEditorV2";
import AdminBlogManager from "../components/AdminBlogManagerV2";
import AdminGalleryManager from "../components/AdminGalleryManager";
import AdminHomepageContentManager from "../components/AdminHomepageContentManager";
import AdminWhatsAppSettings from "../components/AdminWhatsAppSettings";
import AdminEmployerManager from "../components/AdminEmployerManagerV2";
import AdminStudentsManager from "../components/AdminStudentsManagerV2";
import AdminApplicationsManager from "../components/AdminApplicationsManagerV2";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const initialCompany = { name: "Apprenticewala", address: "", phone: "", email: "", website: "", header_logo_url: "" };

export default function AdminWorkspacePage() {
  const { user, setUser, ready } = useAdminAuth();
  const [overview, setOverview] = useState(null);
  const [activeTab, setActiveTab] = useState("jobs");
  const [editingJob, setEditingJob] = useState(null);
  const [jobFormOpen, setJobFormOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState("");
  const [company, setCompany] = useState(initialCompany);

  const loadOverview = useCallback(async () => {
    const data = await apiRequest("/admin/overview");
    setOverview(data);
    setCompany({ ...initialCompany, ...data.company });
  }, []);

  useEffect(() => {
    if (user) loadOverview().catch((error) => setNotice(error.message));
  }, [loadOverview, user]);

  if (!ready) return <main className="admin-page" data-testid="admin-page-loading">Loading workspace…</main>;
  if (!user) return <Navigate to="/admin/login" replace />;

  const saveJob = async (payload) => {
    setSaving(true);
    setNotice("");
    try {
      await apiRequest(editingJob ? `/admin/jobs/${editingJob.id}` : "/admin/jobs", {
        method: editingJob ? "PUT" : "POST",
        body: JSON.stringify(payload),
      });
      await loadOverview();
      setJobFormOpen(false);
      setEditingJob(null);
      setNotice("Job saved successfully.");
    } catch (error) {
      setNotice(error.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteJob = async (job) => {
    if (!window.confirm(`Delete ${job.title}?`)) return;
    try {
      await apiRequest(`/admin/jobs/${job.id}`, { method: "DELETE" });
      await loadOverview();
      setNotice("Job deleted.");
    } catch (error) {
      setNotice(error.message);
    }
  };

  const saveCompany = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const data = await apiRequest("/admin/company", { method: "PUT", body: JSON.stringify(company) });
      setCompany(data);
      setNotice("Company details updated.");
    } catch (error) {
      setNotice(error.message);
    } finally {
      setSaving(false);
    }
  };

  const downloadApplications = async () => {
    try {
      const response = await fetch(`${API}/admin/applications/export`, { credentials: "include" });
      if (!response.ok) throw new Error("Could not prepare the Excel file.");
      const blob = await response.blob();
      const fileUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = "apprenticewala-applications.xlsx";
      link.click();
      URL.revokeObjectURL(fileUrl);
    } catch (error) {
      setNotice(error.message);
    }
  };

  const logout = async () => {
    await apiRequest("/auth/logout", { method: "POST" });
    setUser(null);
  };

  const stats = [
    { label: "Live jobs", value: overview?.jobs.length || 0, icon: BriefcaseBusiness },
    { label: "Applications", value: overview?.applications.length || 0, icon: UsersRound },
    { label: "Company requests", value: overview?.employer_enquiries.length || 0, icon: ClipboardList },
  ];

  return (
    <main className="admin-page" data-testid="admin-page">
      <header className="admin-header">
        <div>
          <span className="eyebrow">Private workspace</span>
          <h1>Good to see you, {user.name}.</h1>
          <p>Manage the opportunities and messages behind Apprenticewala.</p>
        </div>
        <button className="outline-button" onClick={logout} data-testid="admin-logout-button"><LogOut size={17} /> Sign out</button>
      </header>
      <section className="admin-stats" data-testid="admin-stats">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return <div className="admin-stat" key={stat.label} data-testid={`admin-stat-${stat.label.toLowerCase().replaceAll(" ", "-")}`}><Icon size={20} /><strong>{stat.value}</strong><span>{stat.label}</span></div>;
        })}
      </section>
      <div className="admin-tabs" data-testid="admin-tabs">
        {[["jobs", "Jobs"], ["applications", "Applications"], ["requests", "Company requests"], ["employers", "Employers"], ["students", "Student Import"], ["content", "Content Manager"], ["blog", "Blog Manager"], ["gallery", "Gallery Manager"], ["settings", "Settings"], ["company", "Company details"]].map(([id, label]) => <button key={id} type="button" className={activeTab === id ? "admin-tab active" : "admin-tab"} onClick={() => setActiveTab(id)} data-testid={`admin-tab-${id}`}>{label}</button>)}
      </div>
      {notice && <p className="admin-notice" data-testid="admin-notice">{notice}</p>}
      {activeTab === "jobs" && <section className="admin-panel" data-testid="admin-jobs-panel"><div className="admin-panel-heading"><div><h2>Published opportunities</h2><p>Edit roles that students see on the portal.</p></div><button className="primary-button" onClick={() => { setEditingJob(null); setJobFormOpen(true); }} data-testid="admin-new-job-button"><Plus size={17} /> New job</button></div>{jobFormOpen && <AdminJobEditor job={editingJob} saving={saving} onSave={saveJob} onCancel={() => { setJobFormOpen(false); setEditingJob(null); }} />}{overview && <div className="admin-list">{overview.jobs.map((job) => <article className="admin-row" key={job.id} data-testid={`admin-job-row-${job.id}`}><div><span className={`scheme-badge scheme-${job.scheme.toLowerCase().replaceAll(" ", "-")}`}>{job.scheme}</span><h3>{job.title}</h3><p>{job.company} · {job.location} · {job.openings} openings</p><span className="admin-facilities">{job.facilities?.length ? job.facilities.join(" · ") : "No facilities added"}</span></div><div className="row-actions"><button className="icon-button" onClick={() => { setEditingJob(job); setJobFormOpen(true); }} aria-label={`Edit ${job.title}`} data-testid={`admin-edit-job-${job.id}`}><Pencil size={17} /></button><button className="icon-button danger" onClick={() => deleteJob(job)} aria-label={`Delete ${job.title}`} data-testid={`admin-delete-job-${job.id}`}><Trash2 size={17} /></button></div></article>)}</div>}</section>}
      {activeTab === "applications" && <AdminApplicationsManager applications={overview?.applications || []} onChanged={loadOverview} onNotice={setNotice} onDownload={downloadApplications} />}
      {activeTab === "requests" && <section className="admin-panel" data-testid="admin-requests-panel"><div className="admin-panel-heading"><div><h2>Company hiring requests</h2><p>Inbound requirements from companies seeking skilled candidates.</p></div></div><div className="admin-list">{overview?.employer_enquiries.map((item) => <article className="admin-row request-row" key={item.id} data-testid={`admin-enquiry-row-${item.id}`}><div><h3>{item.company_name} <small>{item.hiring_for}</small></h3><p><MapPin size={15} /> {item.location} · {item.openings} openings · {item.contact_name} · {item.phone}</p><span>{item.email}</span></div><time>{new Date(item.created_at).toLocaleDateString()}</time></article>) || <p>Loading company requests…</p>}</div></section>}
      {activeTab === "employers" && <AdminEmployerManager employers={overview?.employers || []} onChanged={loadOverview} onNotice={setNotice} />}
      {activeTab === "students" && <AdminStudentsManager studentsCount={overview?.students_count || 0} upgradeEnquiries={overview?.upgrade_enquiries || []} onChanged={loadOverview} onNotice={setNotice} />}
      {activeTab === "content" && <AdminHomepageContentManager content={overview?.homepage_content} onChanged={loadOverview} onNotice={setNotice} />}
      {activeTab === "blog" && <AdminBlogManager posts={overview?.blog_posts || []} onChanged={loadOverview} onNotice={setNotice} />}
      {activeTab === "gallery" && <AdminGalleryManager items={overview?.gallery_items || []} onChanged={loadOverview} onNotice={setNotice} />}
      {activeTab === "settings" && <AdminWhatsAppSettings company={overview?.company} onChanged={loadOverview} onNotice={setNotice} />}
      {activeTab === "company" && <section className="admin-panel" data-testid="admin-company-panel"><div className="admin-panel-heading"><div><h2>Company contact details</h2><p>These details appear in the website header and footer.</p></div></div><form className="company-form" onSubmit={saveCompany} data-testid="admin-company-form"><label>Company name<input required value={company.name} onChange={(event) => setCompany({ ...company, name: event.target.value })} data-testid="admin-company-name-input" /></label><label>Header call number<input value={company.phone} onChange={(event) => setCompany({ ...company, phone: event.target.value })} data-testid="admin-company-phone-input" /></label><label>Email address<input type="email" value={company.email} onChange={(event) => setCompany({ ...company, email: event.target.value })} data-testid="admin-company-email-input" /></label><label>Website<input value={company.website} onChange={(event) => setCompany({ ...company, website: event.target.value })} data-testid="admin-company-website-input" /></label><label className="form-full-width">Header logo image URL<input type="url" value={company.header_logo_url} onChange={(event) => setCompany({ ...company, header_logo_url: event.target.value })} data-testid="admin-company-logo-url-input" /></label><label className="form-full-width">Address<textarea value={company.address} onChange={(event) => setCompany({ ...company, address: event.target.value })} data-testid="admin-company-address-input" /></label><button className="primary-button form-full-width" disabled={saving} data-testid="admin-company-save-button">{saving ? "Saving…" : "Save company details"}</button></form></section>}
    </main>
  );
}